import { IS_HIDE_PRO_MONTHLY_OPTION } from '@/components/constants/flags';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { cn } from '@/lib/cn';
import { Period } from '@heidi/commerce/contracts';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { type ReactNode, type Ref, useRef, useState } from 'react';
import { UpgradeTestimonialBg } from './upgrade-testimonial-bg';

type IconComponent = React.ComponentType<{ size?: number | string; className?: string }>;

/**
 * New standardized modal for clinician and evidence upgrade modals.
 * Based on original heidi-pro-modal-base.tsx, but slightly different structure
 * designs: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=7249-23405&m=dev
 */

export interface Feature {
  id?: string;
  icon: IconComponent;
  description?: string;
}
export interface HeidiUpgradeBaseModalProps {
  /** Content to display in the right section when hideTestimonial is false. Pass a component (e.g. <div><Image src={...} /></div>). */
  rightPanelContent?: React.ReactNode;
  features?: Array<Feature>;
  yearlySaveBadgeText?: string;
  title?: React.ReactNode;
  yearlyContent: React.ReactNode;
  /** Required when yearlyOnly is false; can be omitted when yearlyOnly is true */
  monthlyContent?: React.ReactNode;
  primaryUpgradeButtonContent?: React.ReactNode;
  secondaryButton?: React.ReactNode;
  onUpgradeClick?: (period: Period) => void;
  isUpgradePending?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  footerText?: ReactNode;
  featuresTitle?: string;
  /** When true, hides the right-hand branding section and uses full-width centered layout */
  hideTestimonial?: boolean;
  variant?: 'pro' | 'practice';
  /** When false, modal cannot be dismissed by clicking outside or pressing Escape. Defaults to true. */
  dismissible?: boolean;
  /** When set, portals the modal into this element instead of body. Use to overlay only a specific area (e.g. main content) while keeping sidebar accessible. */
  portalContainer?: HTMLElement | null;
  /** When true, hides the annual/monthly tabs and shows only yearly pricing. Upgrade always uses yearly period. */
  yearlyOnly?: boolean;
  /** When true, always show the annual/monthly period tabs (e.g. for Evidence Plus and Clinician trial upgrade modals), regardless of IS_HIDE_PRO_MONTHLY_OPTION. Default: false. */
  showPeriodTabs?: boolean;
  /** When true, the primary upgrade button is disabled (e.g. non-admin team members who cannot manage billing). */
  isUpgradeDisabled?: boolean;
  /** Tooltip shown on hover when `isUpgradeDisabled` is true. */
  upgradeDisabledTooltip?: string;
  /** When true, hides the period tabs and price content (e.g. trial CTA variant). */
  hidePricing?: boolean;
  /** Replaces `featuresTitle` + default feature list when the host needs custom content (e.g. usage rows). */
  featuresContent?: React.ReactNode;
  /** Extra classes for the footer caption (e.g. `text-center` for trial-recap layout). Defaults to left-aligned. */
  footerCaptionClassName?: string;
  /**
   * When true, wraps period tabs (if any), pricing, and the features row/list in one rounded
   * bordered panel (trial-recap layout). See Figma node 9985:22876 in Trials — upgrades.
   */
  pricingFeaturesGroupedCard?: boolean;
  /**
   * When set, replaces the primary upgrade button (including disabled + tooltip). Pass a callback
   * that receives a ref assigner for the primary focusable control (e.g. banner CTA button).
   */
  primaryCtaReplacement?: (
    assignPrimaryButtonRef: (el: HTMLElement | null) => void
  ) => ReactNode;
  /** Optional test ID applied to the primary upgrade button. */
  primaryButtonTestId?: string;
}

const FeaturesList = ({ features }: { features: Array<Feature> }) => {
  return (
    <ul className="flex flex-col gap-4" role="list">
      {features.map(({ id, icon: Icon, description }, index) => (
        <li key={id ?? index} className="flex items-center gap-2 text-text-primary">
          <Icon className="size-4 shrink-0 text-text-primary" aria-hidden />
          <TypographyP2>{description}</TypographyP2>
        </li>
      ))}
    </ul>
  );
};

export const HeidiUpgradeBaseModal = ({
  rightPanelContent,
  features,
  yearlySaveBadgeText,
  title,
  footerText,
  yearlyContent,
  monthlyContent,
  open,
  onOpenChange,
  primaryUpgradeButtonContent,
  onUpgradeClick,
  secondaryButton,
  isUpgradePending,
  featuresTitle,
  hideTestimonial,
  variant = 'pro',
  dismissible = true,
  portalContainer,
  yearlyOnly = false,
  showPeriodTabs: forceShowPeriodTabs = false,
  isUpgradeDisabled = false,
  upgradeDisabledTooltip,
  hidePricing = false,
  featuresContent,
  footerCaptionClassName,
  pricingFeaturesGroupedCard = false,
  primaryCtaReplacement,
  primaryButtonTestId,
}: HeidiUpgradeBaseModalProps) => {
  const primaryUpgradeButtonRef = useRef<HTMLElement | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<Period>(Period.ONE_YEAR);
  const effectivePeriod = yearlyOnly ? Period.ONE_YEAR : selectedPeriod;
  const showPeriodTabs =
    !hidePricing && !yearlyOnly && (forceShowPeriodTabs || !IS_HIDE_PRO_MONTHLY_OPTION());
  const showPricingContent = !hidePricing;
  const { t } = useTranslations();

  return (
    <DialogDrawer
      open={open}
      onOpenChange={onOpenChange}
      dismissible={dismissible}
      modal={!portalContainer}
    >
      <DialogDrawerContent
        disableDrag
        dismissible={dismissible}
        portalContainer={portalContainer}
        onOpenAutoFocus={(e) => {
          // Prefer the primary CTA over earlier focusables (e.g. period tabs, links, “Try now”).
          const el = primaryUpgradeButtonRef.current;
          if (!el) return;
          if (el instanceof HTMLButtonElement && el.disabled) return;
          e.preventDefault();
          el.focus();
        }}
        closeIconClassName={
          !hideTestimonial && rightPanelContent ? 'sm:text-text-primary' : undefined
        }
        className={cn(
          'flex max-h-[90dvh] gap-0 overflow-hidden border-none bg-surface p-8',
          hideTestimonial
            ? 'w-[470px] sm:bg-transparent sm:p-0'
            : 'sm:w-11/12 sm:max-w-2xl sm:bg-transparent sm:p-0 md:max-w-3xl lg:max-w-[940px]'
        )}
      >
        <div
          className={cn(
            'flex w-full flex-col justify-center gap-4 bg-surface sm:max-h-full sm:gap-6 sm:p-8',
            hideTestimonial ? 'sm:w-full' : 'sm:w-1/2'
          )}
        >
          {title && (
            <div
              className={cn(
                'w-full shrink-0',
                hideTestimonial && 'flex flex-col items-center text-center'
              )}
            >
              {title}
            </div>
          )}
          <div className="flex w-full flex-col gap-6">
            <div
              className={cn(
                'flex flex-col',
                pricingFeaturesGroupedCard
                  ? 'gap-4 rounded-2xl border border-border p-4'
                  : 'gap-6'
              )}
              data-testid="pricing-features-group"
            >
              {showPeriodTabs && (
                <SegmentedControl
                  value={selectedPeriod}
                  onValueChange={(value) => setSelectedPeriod(value as Period)}
                >
                  <SegmentedControlList className="w-full">
                    <SegmentedControlTrigger value={Period.ONE_YEAR}>
                      {t('upgradeProModal.annual')}
                      <Badge color="green" size="xs">
                        {yearlySaveBadgeText || t('upgradeProModal.savePercent')}
                      </Badge>
                    </SegmentedControlTrigger>
                    <SegmentedControlTrigger value={Period.ONE_MONTH}>
                      {t('upgradeProModal.monthly')}
                    </SegmentedControlTrigger>
                  </SegmentedControlList>
                </SegmentedControl>
              )}
              {showPricingContent &&
                (yearlyOnly ? (
                  <div>{yearlyContent}</div>
                ) : selectedPeriod === Period.ONE_YEAR ? (
                  <div>{yearlyContent}</div>
                ) : (
                  <div>{monthlyContent}</div>
                ))}
              {featuresContent ?? (
                <div className="flex flex-col gap-3">
                  <TypographyP2 className="text-text-primary">
                    {featuresTitle}
                  </TypographyP2>
                  <FeaturesList features={features || []} />
                </div>
              )}
            </div>
          </div>
          <div className="flex w-full flex-col gap-3">
            {primaryCtaReplacement ? (
              primaryCtaReplacement((el) => {
                primaryUpgradeButtonRef.current = el;
              })
            ) : isUpgradeDisabled && upgradeDisabledTooltip ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="w-full">
                      <Button
                        ref={primaryUpgradeButtonRef as Ref<HTMLButtonElement>}
                        className="pointer-events-none w-full"
                        variant={variant === 'pro' ? 'pro' : 'pro_plus'}
                        size="md"
                        disabled
                        data-testid={primaryButtonTestId}
                      >
                        {primaryUpgradeButtonContent}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {upgradeDisabledTooltip}
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button
                ref={primaryUpgradeButtonRef as Ref<HTMLButtonElement>}
                className="w-full"
                variant={variant === 'pro' ? 'pro' : 'pro_plus'}
                size="md"
                disabled={isUpgradePending}
                onClick={() => onUpgradeClick?.(effectivePeriod)}
                data-testid={primaryButtonTestId}
              >
                {primaryUpgradeButtonContent}
              </Button>
            )}
            {secondaryButton ? secondaryButton : null}
            {footerText ? (
              <TypographyCaption
                className={cn(
                  'text-text-secondary',
                  footerCaptionClassName ?? 'text-left'
                )}
              >
                {footerText}
              </TypographyCaption>
            ) : null}
          </div>
        </div>

        {!hideTestimonial && rightPanelContent && (
          <>
            <div
              className="absolute end-0 top-0 z-[-1] hidden h-full w-1/2 overflow-hidden sm:block"
              data-testid="upgrade-modal-bg"
            >
              <UpgradeTestimonialBg
                variant={variant}
                className="absolute inset-0 size-full"
              />
            </div>
            <div
              className="relative hidden min-h-0 w-1/2 flex-1 items-center justify-center p-8 sm:flex"
              data-testid="upgrade-modal-image"
            >
              {rightPanelContent}
            </div>
          </>
        )}
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
